import { TranslateService } from '@ngx-translate/core';
import { IOcrConf } from '../models/ocr';
import { IOcrAllowedDocs, IOcrOperation } from '../models/user-identification';

export function isSpanishCompany(companyCountry?: string | null): boolean {
	return companyCountry === 'ESP';
}

function normalizeCountryCodes(codes?: Array<string> | null): string[] | null {
	if (!Array.isArray(codes)) {
		return null;
	}
	return codes.map((code) => String(code).trim().toUpperCase()).filter(Boolean);
}

export function isDniAllowedInOcr(operation?: IOcrOperation | null): boolean {
	return Array.isArray(operation?.config?.allowed_docs?.dni);
}

export function isDniForOtherCountriesInOcr(operation?: IOcrOperation | null): boolean {
	const countries = normalizeCountryCodes(operation?.config?.allowed_docs?.dni);
	if (countries === null) {
		return false;
	}
	if (countries.length === 0) {
		return true;
	}
	return countries.some((code) => code !== 'ESP');
}

export function isDniForSpainInOcr(operation?: IOcrOperation | null): boolean {
	const countries = normalizeCountryCodes(operation?.config?.allowed_docs?.dni);
	if (countries === null) {
		return false;
	}
	if (countries.length === 0) {
		return true;
	}
	return countries.includes('ESP');
}

export function isPassportAllowedInOcr(operation?: IOcrOperation | null): boolean {
	return Array.isArray(operation?.config?.allowed_docs?.passport);
}

export function isPassportForOtherCountriesInOcr(operation?: IOcrOperation | null): boolean {
	const countries = normalizeCountryCodes(operation?.config?.allowed_docs?.passport);
	if (countries === null) {
		return false;
	}
	if (countries.length === 0) {
		return true;
	}
	return countries.some((code) => code !== 'ESP');
}

export function isPassportForSpainInOcr(operation?: IOcrOperation | null): boolean {
	const countries = normalizeCountryCodes(operation?.config?.allowed_docs?.passport);
	if (countries === null) {
		return false;
	}
	if (countries.length === 0) {
		return true;
	}
	return countries.includes('ESP');
}

/** Clave i18n compartida HOME/OCR para el label «DNI europeo» (compañía ESP + DNI extranjero). */
export const OCR_ALLOWED_DNI_EUROPEAN_I18N_KEY = 'OCR.GENERAL.DOCUMENT_TYPE.DNI_EUROPEAN';

export function getOcrAllowedDniText(
	trans: TranslateService,
	operation?: IOcrOperation | null,
	companyCountry?: string | null,
	dniScope?: 'european' | 'spain',
): string {
	if (isSpanishCompany(companyCountry)) {
		if (dniScope === 'spain' && isDniForSpainInOcr(operation)) {
			return trans.instant('DOCUMENTS.TYPES.DNI');
		}
		if (dniScope === 'european' && isDniForOtherCountriesInOcr(operation)) {
			return trans.instant(OCR_ALLOWED_DNI_EUROPEAN_I18N_KEY);
		}
		if (!dniScope) {
			if (isDniForOtherCountriesInOcr(operation)) {
				return trans.instant(OCR_ALLOWED_DNI_EUROPEAN_I18N_KEY);
			}
			if (isDniForSpainInOcr(operation)) {
				return trans.instant('DOCUMENTS.TYPES.DNI');
			}
		}
	}

	let text = trans.instant('OCR.GENERAL.DOCUMENT_TYPE.DOCUMENT_ID');
	if (operation?.config?.allowed_docs?.dni?.includes('ESP')) {
		const dniText = trans.instant('DOCUMENTS.TYPES.DNI');
		text += ` (${dniText})`;
	}
	return text;
}

/** Subtítulo tutorial OCR solo-DNI con regla compañía española alineada a HOME. */
export function getOcrTutorialDniOnlyTitle(
	trans: TranslateService,
	operation?: IOcrOperation | null,
	companyCountry?: string | null,
): string {
	if (isSpanishCompany(companyCountry)) {
		const other = isDniForOtherCountriesInOcr(operation);
		const spain = isDniForSpainInOcr(operation);
		if (other && !spain) {
			return trans.instant(OCR_ALLOWED_DNI_EUROPEAN_I18N_KEY);
		}
		if (spain && !other) {
			return trans.instant('DOCUMENTS.TYPES.DNI');
		}
		if (other) {
			return trans.instant(OCR_ALLOWED_DNI_EUROPEAN_I18N_KEY);
		}
	}
	return trans.instant('OCR.BACK.TUTORIAL_TITLE_DNI_ONLY');
}

/** True mientras no se haya completado ninguna cara del documento. */
export function isFirstSideScan(ocrConf: IOcrConf): boolean {
	return !ocrConf.document.some((doc) => doc.complete);
}

function hasPassportInAllowedDocs(allowedDocs?: IOcrAllowedDocs | null): boolean {
	return (
		Array.isArray(allowedDocs?.passport) ||
		(allowedDocs !== undefined && allowedDocs !== null && 'passport' in allowedDocs)
	);
}

/** Clave i18n del mensaje ONINIT de la primera captura (genérico, sin anverso/reverso). */
export function resolveFirstScanOnInitKey(allowedDocs?: IOcrAllowedDocs | null): string {
	return hasPassportInAllowedDocs(allowedDocs)
		? 'OCR.GENERAL.INSTRUCTIONS.TEXT.ONINIT_ID_PASSPORT'
		: 'OCR.GENERAL.INSTRUCTIONS.TEXT.ONINIT_ID';
}

/**
 * Clave i18n del 2º prompt hasNoDocument en 1ª cara.
 * Alert corto Figma («Encaja el documento»); el ±pasaporte va solo en el título ONINIT.
 */
export function resolveFirstScanOnWarningKey(_allowedDocs?: IOcrAllowedDocs | null): string {
	return 'OCR.GENERAL.INSTRUCTIONS.TEXT.ONWARNING_FIRST_SCAN_ID';
}
